import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getMembershipTiers, createCheckoutSession } from '../api';
import EmbeddedCheckoutModal from '../components/EmbeddedCheckoutModal';

// ─────────────────────────────────────────────────────────────────────────────
// Static per-plan display config
// ─────────────────────────────────────────────────────────────────────────────
const PLAN_STATIC = {
  free: {
    name: 'Free',
    color: 'var(--muted)',
    highlight: false,
    monthlyPrice: 0,
    annualPrice: 0,
    annualSavings: 0,
  },
  basic: {
    name: 'Basic',
    color: 'var(--blue)',
    highlight: false,
    monthlyPrice: 19,
    annualPrice: 199,
    annualSavings: 29,   // 12×19=228 − 199=29
  },
  pro: {
    name: 'Pro',
    color: 'var(--teal)',
    highlight: true,
    monthlyPrice: 49,
    annualPrice: 499,
    annualSavings: 89,   // 12×49=588 − 499=89
  },
};

// Feature rows driven from DB limits, in display order
const LIMIT_FIELDS = [
  { key: 'max_saved_strategies',              label: 'Saved strategies' },
  { key: 'max_backtests_per_day',             label: 'Backtests per day' },
  { key: 'max_ai_generations_per_day',        label: 'AI strategy builds / day' },
  { key: 'max_ai_explanations_per_day',       label: 'AI explanations / day' },
  { key: 'max_analyst_conversations_per_day', label: 'AI Analyst conversations / day' },
  { key: 'max_analyst_messages_per_day',      label: 'AI Analyst messages / day' },
  { key: 'max_builder_chat_conversations_per_day', label: 'AI strategy chat conversations / day' },
  { key: 'max_builder_chat_messages_per_day',      label: 'AI strategy chat messages / day' },
  { key: 'max_exports_per_day',                    label: 'Strategy code exports / day' },
];

// Boolean features on all plans
const BOOL_FEATURES = [
  'Default strategy library',
  'Drag-and-drop strategy builder',
  'Stock, Forex & Crypto backtests',
];

const EXTRA_FEATURES = {
  pro: ['Priority support'],
};

function fmtLimit(val) {
  return val === -1 ? 'Unlimited' : String(val);
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
export default function PricingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, tier, refreshUser } = useAuth();

  const [billing, setBilling] = useState('monthly');
  const [tierData, setTierData] = useState({});
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [checkoutPriceId, setCheckoutPriceId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (location.pathname === '/pricing/success' ||
        new URLSearchParams(location.search).get('session_id')) {
      setSuccess(true);
    }
  }, [location]);

  useEffect(() => {
    getMembershipTiers()
      .then(res => {
        const map = {};
        for (const t of res.data) map[t.tier] = t;
        setTierData(map);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleCta = (planKey) => {
    if (planKey === 'free') {
      if (isAuthenticated) navigate('/strategies');
      else navigate('/auth', { state: { mode: 'signup' } });
      return;
    }

    if (!isAuthenticated) {
      navigate('/auth', { state: { mode: 'signup' } });
      return;
    }

    const tierRow = tierData[planKey];
    if (!tierRow) return;

    const priceId = billing === 'annual'
      ? tierRow.stripe_annual_price_id
      : tierRow.stripe_monthly_price_id;

    if (!priceId) {
      setError('Payments are not yet configured. Please check back soon.');
      return;
    }

    setError('');
    setCheckoutLoading(planKey);
    setCheckoutPriceId(priceId);
  };

  const fetchCheckoutClientSecret = useCallback(async () => {
    const res = await createCheckoutSession({ price_id: checkoutPriceId });
    return res.data.client_secret;
  }, [checkoutPriceId]);

  const closeCheckout = () => {
    setCheckoutLoading(null);
    setCheckoutPriceId(null);
  };

  const handleCheckoutComplete = async () => {
    closeCheckout();
    await refreshUser();
    setSuccess(true);
  };

  const plans = ['free', 'basic', 'pro'];

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '40px 24px' }}>
      <Helmet>
        <title>Pricing — Algovex</title>
        <meta name="description" content="Simple, transparent pricing for Algovex. Start free, or upgrade to Basic ($19/mo) or Pro ($49/mo) for unlimited backtests, AI strategy builds, and more." />
        <meta property="og:title" content="Algovex Pricing — Free, Basic & Pro Plans" />
        <meta property="og:description" content="Start free or upgrade for unlimited algorithmic trading strategy backtests, AI generation, and the AI Analyst. Annual plans save up to 15%." />
        <meta property="og:url" content="https://algovex.ai/pricing" />
        <link rel="canonical" href="https://algovex.ai/pricing" />
      </Helmet>

      {success && (
        <div style={{
          background: 'rgba(20,184,166,0.1)', border: '1px solid var(--teal)',
          borderRadius: 8, padding: '12px 16px', marginBottom: 24,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          color: 'var(--teal)', fontWeight: 600, fontSize: 14,
        }}>
          <span>🎉 Subscription activated! Your plan has been upgraded.</span>
          <button onClick={() => setSuccess(false)} style={{ background: 'none', border: 'none', color: 'var(--teal)', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
        </div>
      )}

      {error && (
        <div style={{
          background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.4)',
          borderRadius: 8, padding: '12px 16px', marginBottom: 24,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          color: '#ef4444', fontSize: 14,
        }}>
          <span>{error}</span>
          <button onClick={() => setError('')} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
        </div>
      )}

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 36 }}>
        <h1 style={{ margin: '0 0 12px', fontSize: 32, fontWeight: 800, color: 'var(--text)' }}>
          Simple, transparent pricing
        </h1>
        <p style={{ margin: '0 auto', fontSize: 15, color: 'var(--muted)', maxWidth: 480 }}>
          Start free. Upgrade when you're ready for more power.
        </p>
      </div>

      {/* Billing toggle */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 40 }}>
        <button
          onClick={() => setBilling('monthly')}
          style={{
            padding: '9px 24px', fontSize: 13, fontWeight: 700,
            borderRadius: '8px 0 0 8px',
            border: '1px solid var(--border)', borderRight: 'none',
            background: billing === 'monthly' ? 'var(--teal)' : 'var(--card)',
            color: billing === 'monthly' ? 'var(--bg)' : 'var(--muted)',
            cursor: 'pointer',
          }}
        >
          Monthly
        </button>
        <button
          onClick={() => setBilling('annual')}
          style={{
            padding: '9px 24px', fontSize: 13, fontWeight: 700,
            borderRadius: '0 8px 8px 0',
            border: '1px solid var(--border)',
            background: billing === 'annual' ? 'var(--teal)' : 'var(--card)',
            color: billing === 'annual' ? 'var(--bg)' : 'var(--muted)',
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 8,
          }}
        >
          Annual
          <span style={{
            background: billing === 'annual' ? 'rgba(255,255,255,0.25)' : 'rgba(20,184,166,0.15)',
            color: billing === 'annual' ? 'var(--bg)' : 'var(--teal)',
            fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 12,
          }}>
            SAVE UP TO 15%
          </span>
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', color: 'var(--muted)', padding: 40 }}>Loading plans…</div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(270px, 1fr))',
          gap: 20,
          alignItems: 'start',
        }}>
          {plans.map((planKey) => {
            const meta = PLAN_STATIC[planKey];
            const data = tierData[planKey] || {};
            const isCurrent = isAuthenticated && tier === planKey;
            const isPaid = planKey !== 'free';

            let priceDisplay, periodDisplay;
            if (planKey === 'free') {
              priceDisplay = '$0';
              periodDisplay = 'forever';
            } else if (billing === 'annual') {
              priceDisplay = `$${meta.annualPrice}`;
              periodDisplay = 'per year';
            } else {
              priceDisplay = `$${meta.monthlyPrice}`;
              periodDisplay = 'per month';
            }

            const limitFeatures = LIMIT_FIELDS.map(f => ({
              label: f.label,
              value: data[f.key] !== undefined ? fmtLimit(data[f.key]) : '—',
            }));
            const boolFeatures = [
              ...BOOL_FEATURES.map(label => ({ label, value: true })),
              ...(EXTRA_FEATURES[planKey] || []).map(label => ({ label, value: true })),
            ];
            const features = [...limitFeatures, ...boolFeatures];

            return (
              <div
                key={planKey}
                style={{
                  background: 'var(--card)',
                  border: meta.highlight
                    ? '2px solid var(--teal)'
                    : isCurrent
                    ? '2px solid var(--blue)'
                    : '1px solid var(--border)',
                  borderRadius: 12,
                  padding: 28,
                  position: 'relative',
                }}
              >
                {meta.highlight && !isCurrent && (
                  <div style={{
                    position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                    background: 'var(--teal)', color: 'var(--bg)',
                    fontSize: 11, fontWeight: 800, letterSpacing: 1,
                    padding: '3px 12px', borderRadius: 20, textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                  }}>
                    Most Popular
                  </div>
                )}
                {isCurrent && (
                  <div style={{
                    position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                    background: 'var(--blue)', color: 'var(--bg)',
                    fontSize: 11, fontWeight: 800, letterSpacing: 1,
                    padding: '3px 12px', borderRadius: 20, textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                  }}>
                    Current Plan
                  </div>
                )}

                {/* Plan header */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{
                    display: 'inline-block',
                    padding: '3px 10px', borderRadius: 6,
                    background: meta.color, color: 'var(--bg)',
                    fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1,
                    marginBottom: 12,
                  }}>
                    {meta.name}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 32, fontWeight: 800, color: 'var(--text)', lineHeight: 1 }}>
                      {priceDisplay}
                    </div>
                    {isPaid && billing === 'annual' && (
                      <div style={{
                        background: 'rgba(20,184,166,0.15)',
                        color: 'var(--teal)',
                        fontSize: 11, fontWeight: 800,
                        padding: '3px 8px', borderRadius: 6,
                        marginBottom: 2, whiteSpace: 'nowrap',
                      }}>
                        SAVE ${meta.annualSavings}/YR
                      </div>
                    )}
                  </div>

                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 5 }}>
                    {periodDisplay}
                    {isPaid && billing === 'monthly' && (
                      <span style={{ marginLeft: 8, color: 'var(--teal)', fontSize: 11, fontWeight: 600 }}>
                        · ${meta.annualPrice}/yr saves ${meta.annualSavings}
                      </span>
                    )}
                  </div>
                </div>

                {/* Feature list */}
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {features.map((f) => (
                    <li key={f.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
                      <span style={{ color: 'var(--muted)' }}>{f.label}</span>
                      {f.value === true ? (
                        <span style={{ color: 'var(--teal)', fontWeight: 700 }}>✓</span>
                      ) : (
                        <span style={{ color: 'var(--text)', fontWeight: 700 }}>{f.value}</span>
                      )}
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                {isCurrent ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{
                      padding: '10px 0', textAlign: 'center',
                      fontSize: 13, fontWeight: 700, color: 'var(--muted)',
                      border: '1px solid var(--border)', borderRadius: 8,
                    }}>
                      Your current plan
                    </div>
                    {isPaid && (
                      <button
                        onClick={() => navigate('/billing')}
                        style={{
                          padding: '9px 0', fontSize: 12, fontWeight: 600,
                          background: 'transparent', color: 'var(--muted)',
                          border: '1px solid var(--border)', borderRadius: 8,
                          cursor: 'pointer',
                        }}
                      >
                        Manage billing →
                      </button>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => handleCta(planKey)}
                    disabled={!!checkoutLoading}
                    style={{
                      width: '100%', padding: '10px 0',
                      fontSize: 13, fontWeight: 700,
                      background: planKey === 'free'
                        ? 'var(--teal)'
                        : planKey === 'basic'
                        ? 'rgba(59,130,246,0.15)'
                        : 'rgba(20,184,166,0.15)',
                      color: planKey === 'free'
                        ? 'var(--bg)'
                        : planKey === 'basic'
                        ? 'var(--blue)'
                        : 'var(--teal)',
                      border: planKey === 'free'
                        ? 'none'
                        : `1px solid ${planKey === 'basic' ? 'var(--blue)' : 'var(--teal)'}`,
                      borderRadius: 8,
                      cursor: checkoutLoading ? 'default' : 'pointer',
                      boxSizing: 'border-box',
                      opacity: checkoutLoading && checkoutLoading !== planKey ? 0.6 : 1,
                    }}
                  >
                    {checkoutLoading === planKey
                      ? 'Opening…'
                      : planKey === 'free'
                      ? (isAuthenticated ? 'Go to Dashboard' : 'Get Started Free')
                      : `Subscribe · ${billing === 'annual' ? `$${meta.annualPrice}/yr` : `$${meta.monthlyPrice}/mo`}`}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p style={{ textAlign: 'center', marginTop: 36, fontSize: 12, color: 'var(--muted)' }}>
        Secure checkout via Stripe. Manage or cancel your subscription anytime from your billing page.
      </p>

      {checkoutLoading && checkoutPriceId && (
        <EmbeddedCheckoutModal
          title="Subscribe"
          fetchClientSecret={fetchCheckoutClientSecret}
          onComplete={handleCheckoutComplete}
          onClose={closeCheckout}
        />
      )}
    </div>
  );
}
